import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOV25UI } from '../../contexts/ov25-ui-context.js';
import { selectModule, CompatibleModule, closeModuleSelectMenu } from '../../utils/configurator-utils.js';
import { ChevronsRight, CornerDownRight, Layers, Move, Ruler, Sofa } from 'lucide-react';
import { Snap2CustomDimensionForm } from './Snap2CustomDimensionForm.js';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '../ui/carousel.js';
import { Variant } from './ProductVariants.js';
import { cn } from '../../lib/utils.js';

export const ModuleBottomPanel: React.FC<{ portalTarget: Element }> = ({ portalTarget }) => {
  const {
    compatibleModules,
    isModulePanelOpen,
    setIsModulePanelOpen,
    selectedModuleType,
    setSelectedModuleType,
    isModuleSelectionLoading,
    setIsModuleSelectionLoading,
    uniqueId,
    getString,
  } = useOV25UI();

  // Mount/animation state
  const [isRendered, setIsRendered] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  
  // Hover state for tooltips
  const [hoveredModule, setHoveredModule] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [customDimsProductId, setCustomDimsProductId] = useState<number | null>(null);
  const [dimsPopoverStyle, setDimsPopoverStyle] = useState<React.CSSProperties | null>(null);
  const [moduleCarouselApi, setModuleCarouselApi] = useState<CarouselApi | null>(null);
  const moduleCardDomRef = useRef<Partial<Record<number, HTMLDivElement | null>>>({});

  // Tab underline animation state
  const tabTypes = ['all', 'middle', 'corner', 'end'];
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [underlineStyle, setUnderlineStyle] = useState({ width: 0, left: 0 });
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Filter modules based on selected type
  const filteredModules = compatibleModules && compatibleModules.length > 0 ? 
    compatibleModules.filter(module => {
      if (selectedModuleType === 'all') return true;
      const position = module.position.toLowerCase();
      return position.includes(selectedModuleType);
    }) : [];

  // Get available tab types based on what modules exist
  const getAvailableTabTypes = () => {
    if (!compatibleModules || compatibleModules.length === 0) return ['all'];
    
    const availableTypes = ['all']; // Always show 'all'
    
    // Check which specific types have modules
    const hasMiddle = compatibleModules.some(module => 
      module.position.toLowerCase().includes('middle')
    );
    const hasCorner = compatibleModules.some(module => 
      module.position.toLowerCase().includes('corner')
    );
    const hasEnd = compatibleModules.some(module => 
      module.position.toLowerCase().includes('end')
    );
    
    if (hasMiddle) availableTypes.push('middle');
    if (hasCorner) availableTypes.push('corner');
    if (hasEnd) availableTypes.push('end');
    
    return availableTypes;
  };

  const availableTabTypes = getAvailableTabTypes();

  const placeMovableForModule = (module: CompatibleModule) => {
    if (isModuleSelectionLoading) return;
    const modelPath = module.model?.modelPath;
    const modelId = module.model?.modelId;
    if (!modelPath || !modelId) return;
    setIsModuleSelectionLoading(true);
    selectModule({ modelPath, modelId, placeMovable: true }, uniqueId);
    setIsModulePanelOpen(false);
    closeModuleSelectMenu(uniqueId);
  };

  const handlePlaceMovable = (e: React.MouseEvent, module: CompatibleModule) => {
    e.stopPropagation();
    placeMovableForModule(module);
  };

  const handleModuleSelect = (variant: Variant) => {
    if (isModuleSelectionLoading) {
      return;
    }

    const module = variant.data as CompatibleModule;
    if (customDimsProductId === module.product.id) {
      return;
    }

    const modelPath = module?.model?.modelPath;
    const modelId = module?.model?.modelId;

    if (!modelPath || !modelId) {
      return;
    }

    setIsModuleSelectionLoading(true);
    selectModule({ modelPath, modelId }, uniqueId);
  };

  const handleClose = () => {
    setCustomDimsProductId(null);
    setIsModulePanelOpen(false);
    closeModuleSelectMenu(uniqueId);
  };

  useLayoutEffect(() => {
    if (customDimsProductId == null) {
      setDimsPopoverStyle(null);
      return;
    }
    const measure = () => {
      const el = moduleCardDomRef.current[customDimsProductId];
      if (!el || typeof window === 'undefined') {
        setDimsPopoverStyle(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const width = Math.min(280, window.innerWidth - 16);
      const centerX = r.left + r.width / 2;
      const leftPx = Math.max(width / 2 + 8, Math.min(centerX, window.innerWidth - width / 2 - 8));
      setDimsPopoverStyle({
        position: 'fixed',
        left: `${leftPx}px`,
        top: `${r.top - 8}px`,
        transform: 'translate(-50%, -100%)',
        width: `${width}px`,
        maxHeight: 'min(50vh, 22rem)',
        overflowY: 'auto',
        zIndex: 2147483640,
        boxSizing: 'border-box',
      });
    };
    measure();
  }, [customDimsProductId, isEntering, selectedModuleType]);

  useEffect(() => {
    if (customDimsProductId == null || !moduleCarouselApi) return;
    const dismiss = () => setCustomDimsProductId(null);
    moduleCarouselApi.on('scroll', dismiss);
    return () => {
      moduleCarouselApi.off('scroll', dismiss);
    };
  }, [moduleCarouselApi, customDimsProductId]);

  // Handle mount/unmount with transition classes; unmount on transition end
  useEffect(() => {
    if (isModulePanelOpen && filteredModules.length > 0) {
      setIsRendered(true);
      const id = requestAnimationFrame(() => setIsEntering(true));
      return () => cancelAnimationFrame(id);
    } else if (isRendered) {
      setIsEntering(false);
    }
  }, [isModulePanelOpen, filteredModules.length, isRendered]);

  // Reset to "All" when compatibleModules changes
  useEffect(() => {
    if (compatibleModules && compatibleModules.length > 0) {
      setSelectedModuleType('all');
      setActiveTabIndex(0);
    }
  }, [compatibleModules, setSelectedModuleType]);

  // Update active tab index when selectedModuleType changes
  useEffect(() => {
    const newIndex = availableTabTypes.indexOf(selectedModuleType);
    if (newIndex !== -1) {
      setActiveTabIndex(newIndex);
    }
  }, [selectedModuleType, availableTabTypes]);

  const updateUnderlinePosition = (tabIndex: number) => {
    const activeTab = tabsRef.current[tabIndex];
    if (activeTab) {
      const { offsetLeft, offsetWidth } = activeTab;
      setUnderlineStyle({
        width: offsetWidth,
        left: offsetLeft
      });
    }
  };

  // Update underline position when active tab changes
  useEffect(() => {
    if (isRendered) {
      updateUnderlinePosition(activeTabIndex);
    }
  }, [activeTabIndex, isRendered]);

  // Ensure underline is positioned after animation completes
  useEffect(() => {
    if (isRendered && underlineStyle.width === 0 && isEntering) {
      // Wait for animation to complete (300ms duration + buffer)
      const timer = setTimeout(() => {
        updateUnderlinePosition(0);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isRendered, isEntering]);

  if (!isRendered) {
    return null;
  }

  const positionClass = portalTarget ? 'ov:absolute' : 'ov:fixed';
  const handleTransitionEnd = () => {
    if (!isEntering && !isModulePanelOpen) {
      setIsRendered(false);
    }
  };

  const panelContent = (
    <div 
      className={`${positionClass} ov:bottom-0 ov:left-0 ov:right-0 ov:h-60 ov:bg-gray-100 ov:shadow-inner-top ov:z-102 ov:border-t ov:border-(--ov25-border-color) ov:transition-transform ov:duration-300 ov:ease-out ov:shadow-inner-top ${isEntering ? 'ov:translate-y-0' : 'ov:translate-y-full'}`}
      style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05), 0 -2px 4px -2px rgba(0,0,0,0.03)' }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="ov:w-full ov:h-full ov:p-4 ov:px-4">
        {/* Module type tabs */}
        <div className="ov:flex ov:justify-between ov:items-center">
          <div className="ov:w-full">
            <div className="ov:flex ov:justify-center ov:mb-4">
              <div className="ov:bg-white ov:shadow-md ov:backdrop-blur-sm ov:rounded-full ov:px-2 ov:pb-0">
                <div className="ov:relative ov:flex ov:items-center ov:gap-1">
                  {availableTabTypes.map((tabType, index) => {
                    const getTabConfig = (type: string) => {
                      switch (type) {
                        case 'all':
                          return { icon: Layers, label: 'All' };
                        case 'middle':
                          return { icon: Sofa, label: 'Middle' };
                        case 'corner':
                          return { icon: CornerDownRight, label: 'Corner' };
                        case 'end':
                          return { icon: ChevronsRight, label: 'End' };
                        default:
                          return { icon: Layers, label: 'All' };
                      }
                    };

                    const { icon: Icon, label } = getTabConfig(tabType);
                    const tabLabel = getString('modulePositionTypeTabLabel', { TAB_LABEL: label, TAB_TYPE: tabType }, label);
                    
                    return (
                      <button
                        key={tabType}
                        ref={(el) => { tabsRef.current[index] = el; }}
                        onClick={() => setSelectedModuleType(tabType as 'all' | 'middle' | 'corner' | 'end')}
                        className={`ov:flex ov:items-center ov:gap-1.5 ov:px-4 ov:py-2 ov:rounded-full ov:text-sm ov:font-medium ov:transition-colors ${
                          selectedModuleType === tabType 
                            ? 'ov:text-gray-900' 
                            : 'ov:text-gray-600 ov:hover:text-gray-900'
                        }`}
                      >
                        <Icon className="ov:w-3.5 ov:h-3.5" />
                        <span>{tabLabel}</span>
                      </button>
                    );
                  })}
                  
                  {/* Animated green underline */}
                  <div
                    className="ov:absolute ov:bottom-0 ov:transition-all ov:duration-300 ov:ease-out"
                    style={{
                      width: `${underlineStyle.width}px`,
                      left: `${underlineStyle.left}px`,
                      height: '4px',
                      backgroundColor: '#64bfb7'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="ov:p-2 ov:hover:bg-(--ov25-accent-color) ov:rounded-full ov:cursor-pointer"
          >
            <svg className="ov:w-5 ov:h-5 ov:text-(--ov25-secondary-text-color)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Module carousel */}
        <div className="ov:flex-1 ov:overflow-visible">
          {filteredModules.length === 0 ? (
            <div className="ov:flex ov:flex-col ov:items-center ov:justify-center ov:py-8 ov:space-y-4">
              <p className="ov:text-sm ov:text-(--ov25-secondary-text-color)">
                {getString('snap2ModulesLoading', undefined, 'Loading...')}
              </p>
            </div>
          ) : (
            <Carousel className="ov:w-full ov:pointer-events-auto" setApi={setModuleCarouselApi}>
              <CarouselContent className="ov:justify-center">
                {filteredModules.map((module) => {
                  const moduleProductName = getString('productName', { PRODUCT_NAME: module.product.name }, module.product.name);
                  const variant: Variant = {
                    id: `${module.productId}-${module.model.modelId}`,
                    name: module.product.name,
                    price: 0,
                    image: module.product.hasImage ? module.product.imageUrl : '/placeholder.svg?height=200&width=200',
                    blurHash: '',
                    data: module,
                    isSelected: false
                  };

                  const hasVariableDims = Boolean(
                    module.variableDimensions?.x ||
                      module.variableDimensions?.y ||
                      module.variableDimensions?.z
                  );
                  const isMovable = Boolean(module.isMovable);

                  return (
                    <CarouselItem key={module.productId} className="ov:basis-[150px] ov:shrink-0">
                      <div 
                        ref={(el) => {
                          if (el) moduleCardDomRef.current[module.product.id] = el;
                          else delete moduleCardDomRef.current[module.product.id];
                        }}
                        className={cn(
                          'ov:p-2 ov:flex ov:flex-col ov:gap-2 ov:items-center ov:z-50 ov:w-[150px] ov:relative ov:min-h-[150px] ov:overflow-visible',
                          isModuleSelectionLoading ? 'ov:cursor-not-allowed ov:opacity-50' : 'ov:cursor-pointer'
                        )}
                        onClick={() => handleModuleSelect(variant)}
                        onMouseEnter={(e) => {
                          const moduleId = `${module.productId}-${module.model.modelId}`;
                          // Get module position for tooltip placement
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPosition({
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                          
                          setHoveredModule(moduleId);
                        }}
                        onMouseLeave={() => {
                          setHoveredModule(null);
                        }}
                      >
                        {isMovable && (
                          <button
                            type="button"
                            aria-label="Place Movable"
                            onClick={(e) => handlePlaceMovable(e, module)}
                            className={cn(
                              'ov:absolute ov:top-1 ov:z-30 ov:flex ov:h-7 ov:w-7 ov:items-center ov:justify-center ov:rounded-lg ov:border-0 ov:bg-white ov:shadow-md ov:cursor-pointer',
                              hasVariableDims ? 'ov:right-9' : 'ov:right-1'
                            )}
                          >
                            <Move className="ov:h-3.5 ov:w-3.5 ov:text-(--ov25-text-color)" />
                          </button>
                        )}
                        {hasVariableDims && (
                          <button
                            type="button"
                            aria-label="Custom Size"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomDimsProductId((prev) =>
                                prev === module.product.id ? null : module.product.id
                              );
                            }}
                            className="ov:absolute ov:top-1 ov:right-1 ov:z-30 ov:flex ov:h-7 ov:w-7 ov:items-center ov:justify-center ov:rounded-lg ov:border-0 ov:bg-white ov:shadow-md ov:cursor-pointer"
                          >
                            <Ruler className="ov:h-3.5 ov:w-3.5 ov:text-(--ov25-text-color)" />
                          </button>
                        )}
                        <div 
                          className="ov:w-[100px] ov:h-[100px] ov:flex ov:items-center ov:justify-center ov:rounded-xl ov:shadow-lg ov:bg-white ov:cursor-pointer ov:p-2"
                        >
                          {module.product.hasImage && module.product.imageUrl ? (
                            <img
                              src={module.product.imageUrl}
                              alt={moduleProductName}
                              className="ov:w-full ov:h-full ov:object-contain ov:rounded-lg"
                            />
                          ) : (
                            <div className="ov:w-full ov:h-full ov:bg-gray-200 ov:rounded-lg ov:flex ov:items-center ov:justify-center">
                              <span className="ov:text-gray-400 ov:text-xs">{getString('moduleCardNoImage', undefined, 'No Image')}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Same shadow root as panel (portalTarget) so ov:* applies */}
                        {hoveredModule === `${module.productId}-${module.model.modelId}` &&
                          createPortal(
                            <div
                              className="ov:fixed ov:px-3 ov:py-2 ov:bg-gray-900 ov:text-white ov:text-sm ov:rounded-lg ov:shadow-lg ov:z-2147483646 ov:pointer-events-none ov:whitespace-nowrap"
                              style={{
                                left: `${tooltipPosition.x}px`,
                                top: `${tooltipPosition.y}px`,
                                transform: 'translateX(-50%)',
                              }}
                            >
                              {moduleProductName}
                            </div>,
                            portalTarget
                          )}
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>
          )}
        </div>
      </div>
    </div>
  );

  const dimsModule =
    customDimsProductId != null
      ? filteredModules.find((m) => m.product.id === customDimsProductId)
      : undefined;

  return (
    <>
      {createPortal(panelContent, portalTarget)}
      {dimsModule != null &&
        dimsPopoverStyle != null &&
        createPortal(
          <div style={dimsPopoverStyle}>
            <Snap2CustomDimensionForm
              module={dimsModule}
              onCancel={() => setCustomDimsProductId(null)}
              onConfirm={(customDimensions) => {
                const modelPath = dimsModule.model?.modelPath;
                const modelId = dimsModule.model?.modelId;
                if (!modelPath || !modelId) return;
                setIsModuleSelectionLoading(true);
                selectModule({ modelPath, modelId, customDimensions }, uniqueId);
                setCustomDimsProductId(null);
              }}
            />
          </div>,
          portalTarget
        )}
    </>
  );
};
